import React, { useMemo, useEffect, useState } from 'react';
import { Doughnut } from 'react-chartjs-2';
import 'chart.js/auto';
import { Chart as ChartJS } from 'chart.js';
import { useHistory } from 'react-router-dom';

// Define the centerText plugin for Chart.js v4
const centerTextPlugin = {
    id: 'centerText',
    beforeDraw: function(chart) {
        try {
            // Ensure chart and context are available
            if (!chart || !chart.ctx || !chart.width || !chart.height) {
                return;
            }
            
            const width = chart.width;
            const height = chart.height;
            const ctx = chart.ctx;
            const totalValue = chart.config?.options?.plugins?.centerText?.total || 0;
            
            ctx.restore();
            
            // Text Styles
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            
            // First Line (Total Label)
            ctx.font = '500 16px Arial';
            ctx.fillStyle = '#A0A0A0';
            ctx.fillText('Total Records', width / 2, height / 2 - 15);
            
            // Second Line (Total Value)
            ctx.font = 'bold 28px Arial';
            ctx.fillStyle = '#000';
            ctx.fillText(totalValue.toLocaleString(), width / 2, height / 2 + 15);
            
            ctx.save();
        } catch (error) {
            // console.warn removed
        }
    }
};

// Global flag to track if Chart.js is properly initialized
let chartJSInitialized = false;

const DonutChart = ({ series = [], label = [], colour = [] }) => {
    const history = useHistory();
    const [chartReady, setChartReady] = useState(false);
    const [chartError, setChartError] = useState(false);
    
    // Calculate validSeries and total using useMemo to prevent recalculations
    const { validSeries, total } = useMemo(() => {
        const validData = Array.isArray(series) 
            ? series.map(num => typeof num === 'string' ? parseFloat(num) : num)
                  .filter(num => typeof num === 'number' && !isNaN(num))
            : [];
        
        return {
            validSeries: validData,
            total: validData.reduce((acc, curr) => acc + curr, 0)
        };
    }, [series]);

    // Create chart data using useMemo
    const data = useMemo(() => ({
        labels: label,
        datasets: [
            {
                data: validSeries.length > 0 ? validSeries : [0],
                backgroundColor: colour?.length > 0 ? colour : ['#1DBF73', '#FF9F0A', '#FB3B52', '#E2E0FF'],
                hoverBackgroundColor: colour?.length > 0 ? colour : ['#17A65D', '#E88C00', '#D92D45', '#C2C0FF'],
                borderWidth: 2,
            },
        ],
    }), [validSeries, label, colour]);

    const options = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        cutout: '70%',
        plugins: {
            legend: { display: false },
            tooltip: { enabled: true },
            centerText: {
                total: total
            }
        },
        // onClick: (event, elements) => {
        //     try {
                
        //         if (elements && elements.length > 0) {
        //             const clickedElement = elements[0];
        //             const dataIndex = clickedElement.index;
        //             const selectedLabel = label[dataIndex];
        //             const selectedValue = validSeries[dataIndex];
                    
        //                 label: selectedLabel,
        //                 value: selectedValue,
        //                 index: dataIndex
        //             });
                    
        //             const port = window.location.port || (window.location.protocol === 'https:' ? '443' : '80');
        //             history.push(`/assets?port=${port}&selected=${selectedLabel}&value=${selectedValue}`);
        //         }
        //     } catch (error) {
        //         // console.warn removed
        //     }
        // }
    }), [label, validSeries, total, history]);

    // Register the plugin globally once with better error handling
    useEffect(() => {
        let isMounted = true;
        let retryCount = 0;
        const maxRetries = 3;
        
        const initializeChart = async () => {
            try {
                // Check if ChartJS is properly loaded and has the expected structure
                if (ChartJS && typeof ChartJS.register === 'function' && ChartJS.registry) {
                    // Check if our plugin is already registered
                    const existingPlugin = ChartJS.registry.plugins?.get('centerText');
                    if (!existingPlugin) {
                        ChartJS.register(centerTextPlugin);
                    }
                    
                    if (isMounted) {
                        chartJSInitialized = true;
                        setChartReady(true);
                        setChartError(false);
                    }
                } else if (retryCount < maxRetries) {
                    retryCount++;
                    // Retry after a short delay
                    setTimeout(() => {
                        if (isMounted) {
                            initializeChart();
                        }
                    }, 300);
                } else {
                    // Give up and render without the plugin
                    if (isMounted) {
                        setChartReady(true);
                        setChartError(true);
                    }
                }
            } catch (error) {
                // console.warn removed
                if (isMounted) {
                    setChartReady(true);
                    setChartError(true);
                }
            }
        };

        initializeChart();

        // Cleanup function
        return () => {
            isMounted = false;
            try {
                if (ChartJS && ChartJS.registry && ChartJS.registry.plugins) {
                    const plugin = ChartJS.registry.plugins.get('centerText');
                    if (plugin) {
                        ChartJS.registry.plugins.remove('centerText');
                    }
                }
            } catch (error) {
                // console.warn removed
            }
        };
    }, []);

    // Add error boundary for the chart component
    try {
        if (!chartReady) {
            return (
                <div style={{ height: '240px', width: '240px', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span>Loading chart...</span>
                </div>
            );
        }

        if (chartError || !chartJSInitialized) {
            // Render a simplified version without the custom plugin
            const fallbackOptions = {
                ...options,
                plugins: {
                    ...options.plugins,
                    centerText: undefined // Remove the custom plugin
                }
            };
            
            return (
                <div style={{ height: '240px', width: '240px', position: 'relative' }}>
                    <Doughnut 
                        data={data} 
                        options={fallbackOptions}
                    />
                </div>
            );
        }

        return (
            <div style={{ height: '240px', width: '240px', position: 'relative' }}>
                <Doughnut 
                    data={data} 
                    options={options}
                />
            </div>
        );
    } catch (error) {
        // console.error removed
        return (
            <div style={{ height: '240px', width: '240px', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span>Chart loading...</span>
            </div>
        );
    }
};

export default DonutChart;